import { StorageAdapter } from '../storage/StorageAdapter'
import { rowsOf } from '../storage/rows'
import { getDefaultBackend } from './PhotoStore'
import { dbg, dbgWarn } from '../logger'

/**
 * photos/purge.js — barrido idempotente post-drain que purga binarios tras subida CONFIRMADA
 * (MOD-05 purge-after-confirmed-upload).
 *
 * NO va dentro de commitSuccess/success-write (Pitfall 2 + constraint): un borrado de FS NO es
 * transaccional con SQLite (no puede hacer rollback dentro de la tx del commit). Por eso es un
 * sweep SEPARADO, idempotente, llamado tras drain() en sync-worker (espeja revalidateStale).
 *
 * PREDICADO DE PURGA (conservador, anti-perdida — Pitfall 2):
 *   Una fila photo_meta es purgable cuando, para su parent_uuid:
 *     (a) NO existe ninguna fila de outbox pending/in_flight/DEAD (ningun op sin confirmar la referencia), Y
 *     (b) SI existe al menos una fila de outbox 'done'            (la subida fue CONFIRMADA).
 *   Asi NUNCA se purga antes de confirmar (a sin b = aun no subio / dead-letter no confirmado) y
 *   NUNCA mientras un op pendiente/en-vuelo/dead pueda reintentar usando el binario.
 *
 *   FIX pérdida-silenciosa (done+dead): un op 'dead' DEBE bloquear la purga. Caso: foto A sube y su op
 *   queda 'done'; foto B reemplaza (mismo parent_uuid) y SU op va a 'dead' (4xx/422). Si la purga solo
 *   mirara pending/in_flight, veria done(A) sin pendientes y BORRARIA el binario de B —nunca subido—,
 *   volteando upload_status='done': perdida silenciosa irrecuperable. Incluir 'dead' lo conserva para
 *   que retryDead lo re-suba intacto.
 *
 *   FIX pérdida-silenciosa (done VIEJO de OTRA foto, blocker review Hugo): el parent_uuid es
 *   DETERMINISTA (ans:<occ>:<item>), así que un 'done' de la foto A NO garantiza que subió la foto B
 *   (re-toma con el mismo parent). Tres capas conservadoras:
 *     (1) TEMPORAL: el 'done' que confirma debe ser POSTERIOR a la captura de ESTA foto
 *         (o.created_at > pm.created_at). Formatos distintos — pm.created_at es ISO string,
 *         outbox.created_at es epoch ms — así que se convierte pm a epoch ms con julianday (precisión ms).
 *     (2) GRACE WINDOW: NO purgar fotos capturadas hace < graceMs (espejo del sweep de huérfanas): mata
 *         la variante SIN crash (sweep post-drain corriendo entre capture() y el commit de saveAnswer()).
 *     (3) 'pending' Y 'missing' [review Hugo #2]: una foto 'missing' (binario irrecuperable al enviar,
 *         reportMissingPhotos) NO tiene retry que la resucite — su op ya quedó 'done' (commitSuccess
 *         corrió antes) y una re-toma usa un image_ref NUEVO. La pérdida ya se hizo visible via la nota
 *         photo_missing; dejar su binario/fila en disco solo fuga recursos. Se purga con los MISMOS 3
 *         guards (una 'missing' legítima los satisface: op done AT-OR-AFTER su captura, sin op vivo).
 *
 * Cada fila: backend.del (borra binario, idempotente) + UPDATE photo_meta upload_status='done'
 * (idempotente). Un crash a mitad del sweep se auto-sana en el siguiente barrido (ambas ops son
 * idempotentes). SQL SOLO via el `db` inyectado (default = StorageAdapter, STOR-05).
 *
 * @param {{ backend?: object, db?: object, now?: number, graceMs?: number }} deps
 * @returns {Promise<{ purged: number }>}
 */
// pm.created_at (ISO string) -> epoch ms, precisión de milisegundos (julianday tolera 'Z' y fracción).
const PM_EPOCH_MS = '((julianday(pm.created_at) - 2440587.5) * 86400000.0)'

// Retención MÍNIMA tras el done (v8, carrera de reingreso): el binario debe sobrevivir al menos esto
// desde que su op flipó a done. Sin ella, la purga corría en el MISMO tick que el drain y una foto
// recién subida perdía su binario en segundos -> un find() cuyo vuelo cabalgaba el fin del drain traía
// un snapshot del server SIN la foto y el overlay (que la pinta desde el binario local, ver
// listPendingAnswerPhotos) ya no tenía qué resolver -> foto invisible hasta el siguiente reingreso
// (reproducida en ui-photo-reentry-race.smoke.mjs). 5 min cubren con holgura cualquier find() en vuelo
// y el costo es minutos extra de disco por foto ya subida.
export const DONE_GRACE_MS = 5 * 60000

export async function purgeUploaded ({ backend = getDefaultBackend(), db = StorageAdapter, now = Date.now(), graceMs = 60000 } = {}) {
  const cutoffIso = new Date(now - graceMs).toISOString()
  const res = await db.query(
    "SELECT image_ref, parent_uuid, local_path FROM photo_meta pm " +
    // 'missing' se purga también [review Hugo #2]. 'uploaded' es el sello que pone el handler cuando
    // el binario viajó en un chunk 2xx (TKT-2240): es el caso MÁS purgable que existe — el server ya
    // lo tiene — así que dejarlo fuera habría acumulado binarios en disco para siempre.
    "WHERE pm.upload_status IN ('pending','missing','uploaded') " +
    "AND pm.parent_uuid IS NOT NULL " +
    "AND pm.created_at < ? " + // grace window: no purgar capturas recientes (< graceMs)
    "AND NOT EXISTS (SELECT 1 FROM outbox o WHERE o.client_uuid = pm.parent_uuid AND o.status IN ('pending','in_flight','dead')) " +
    // el 'done' que confirma debe ser AT-OR-AFTER la captura de ESTA foto: el op siempre se encola
    // DESPUÉS de capture() (capture escribe photo_meta, luego el repo inserta el op), así que un done
    // >= la captura SÍ incluyó esta foto; el done de una re-toma PREVIA es estrictamente anterior a esta
    // captura (photos secuenciales, mismo parent) y queda excluido. (>= tolera el borde encolar==capturar.)
    // Y ADEMÁS debe llevar done ≥ DONE_GRACE_MS (ver arriba). COALESCE(done_at,0): las filas done
    // legacy (pre-v8, sin done_at) llevan done desde antes del deploy -> purgables ya.
    "AND EXISTS (SELECT 1 FROM outbox o WHERE o.client_uuid = pm.parent_uuid AND o.status = 'done' AND o.created_at >= " + PM_EPOCH_MS + " AND COALESCE(o.done_at, 0) <= ?)",
    [cutoffIso, now - DONE_GRACE_MS]
  )
  const rows = rowsOf(res)

  let purged = 0
  const purgedRefs = []
  for (const row of rows) {
    try {
      await backend.del(row) // borra el binario (idempotente: not-found = no-op)
      await db.run("UPDATE photo_meta SET upload_status='done' WHERE image_ref=?", [row.image_ref])
      purged++
      purgedRefs.push(row.image_ref)
    } catch (e) {
      // best-effort por fila: un fallo aislado no detiene el sweep; se reintenta en el proximo tick.
      dbgWarn('photo', `purge row skipped (${row.image_ref})`, e)
    }
  }
  // Miga para el diagnóstico de QA: si una foto "se pierde", saber si fue el purge quien la borró
  // (y cuáles refs) distingue un borrado legítimo post-confirmación de una pérdida real.
  if (purged > 0) dbg('photo', `purge: ${purged} binario(s) borrados tras confirmación`, { refs: purgedRefs })
  return { purged }
}
